<template>
  <div
    class="app-shell relative flex h-screen w-full flex-col overflow-x-hidden text-text-primary text-white-primary"
    :style="{
      '--license-bar-height': showLicenseBar ? LICENSE_BAR_HEIGHT : '0px',
    }"
  >
    <GuestOverlay v-if="isGuest" />
    <LicenseExpiredOverlay v-else-if="showExpiredOverlay" />
    <template v-else>
      <!-- The bar takes real layout space, and pages own their scroll — so
           this shell must not scroll too. -->
      <LicenseTopBar
        class="overflow-hidden apple-layout"
        :class="
          showLicenseBar
            ? 'h-12 opacity-100'
            : 'pointer-events-none h-0 opacity-0'
        "
        :aria-hidden="!showLicenseBar"
      />
      <div class="relative min-h-0 w-full flex-1 overflow-hidden">
        <div class="h-full">
          <RouterView />
        </div>
        <Sidebar v-if="!isAuthRoute" />
        <LicenseExpiryModal v-if="!isAuthRoute" />
        <Alert />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from "vue";
import Sidebar from "./components/layout/Sidebar.vue";
import router from "./router";
import { useI18n } from "vue-i18n";
import { applyAppLocale } from "./i18n";
import { useUserSettingsStore } from "@/stores/settings/user";
import { useLicenseStore } from "@/stores/settings/license";
import { useAuthStore } from "@/stores/auth/session";
import type { UserParameters } from "@/types";
import { UserRole } from "@/types";
import { notifyFromCatch, parseAccessToken } from "@/helpers";
import { storeToRefs } from "pinia";
import Alert from "./components/ui/overlays/Alert.vue";
import LicenseTopBar from "./components/layout/LicenseTopBar.vue";
import LicenseExpiryModal from "./components/layout/LicenseExpiryModal.vue";
import LicenseExpiredOverlay from "./components/layout/LicenseExpiredOverlay.vue";
import GuestOverlay from "./components/overlays/GuestOverlay.vue";

const { t } = useI18n();
const userSettingsStore = useUserSettingsStore();
const licenseStore = useLicenseStore();

const authStore = useAuthStore();
const { token: tokenInfo } = storeToRefs(authStore);

const isGuest = computed(() => tokenInfo.value?.roles.includes(UserRole.Guest));

const isAuthRoute = computed(() =>
  authRoutes.includes(router.currentRoute.value.path),
);

// 3rem matches the open bar (h-12). Fixed side panels anchor to the viewport,
// so they read this CSS var to clear the bar.
const LICENSE_BAR_HEIGHT = "3rem";

// Never on auth screens: there is no session there yet.
const showExpiredOverlay = computed(
  () => !isAuthRoute.value && licenseStore.isExpired,
);
const showLicenseBar = computed(
  () => !isAuthRoute.value && licenseStore.isExpiringSoon,
);

async function applyLocaleFromUserParams(params: UserParameters) {
  const storageLocale = localStorage.getItem("locale");
  const rawLocale =
    storageLocale !== null && storageLocale !== ""
      ? storageLocale
      : params.locale;
  await applyAppLocale(rawLocale);
}

async function ensureUserSettingsLoaded() {
  if (authRoutes.includes(router.currentRoute.value.path)) return;
  if (parseAccessToken() == null) return;
  if (userSettingsStore.isLoaded || userSettingsStore.isLoading) return;

  try {
    const params = await userSettingsStore.load();
    applyLocaleFromUserParams(params);
  } catch (e) {
    notifyFromCatch(e, t("errors.unknown_error"));
  }
}

// Loaded for every authenticated role, not just those who can see the license
// page — an expired license blocks the product for all of them. Kept apart from
// the user-settings load so it still runs if that one fails, and failures are
// silent: the warnings just don't show rather than nagging.
async function ensureLicenseLoaded() {
  if (authRoutes.includes(router.currentRoute.value.path)) return;
  if (parseAccessToken() == null) return;
  try {
    await licenseStore.load();
  } catch {
    /* warnings stay hidden */
  }
}

onMounted(() => {
  const storageLocale = localStorage.getItem("locale");
  if (storageLocale !== null && storageLocale !== "") {
    void applyAppLocale(storageLocale);
  }
  void ensureUserSettingsLoaded();
  void ensureLicenseLoaded();
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChangeFlushMeta);
});

onUnmounted(() => {
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("visibilitychange", onVisibilityChangeFlushMeta);
});

function onPageHide() {
  void userSettingsStore.flushMetaPut();
}

function onVisibilityChangeFlushMeta() {
  if (document.visibilityState === "hidden") {
    void userSettingsStore.flushMetaPut();
  }
}

watch(
  () => router.currentRoute.value.path,
  () => {
    void ensureUserSettingsLoaded();
    void ensureLicenseLoaded();
  },
);

const authRoutes = [
  "/login",
  "/register",
  "/forgot-password",
  "/restore-password",
];
</script>
